/**
 * Utility functions for articulation analysis calculations and data processing
 */

/**
 * Filter sessions by number of days
 * @param {Array} sessions - All sessions
 * @param {number|string} days - Number of days (1, 7, 30, or 'trend')
 * @returns {Array} Filtered sessions
 */
export const filterSessionsByDays = (sessions, days) => {
  if (!sessions || sessions.length === 0) return [];
  
  const now = new Date();
  const daysNum = days === 'trend' ? 30 : parseInt(days);
  const cutoff = new Date(now.getTime() - daysNum * 24 * 60 * 60 * 1000);
  
  return sessions.filter(s => {
    const sessionDate = new Date(s.created_at);
    return sessionDate >= cutoff;
  });
};

/**
 * Filter sessions by exercise type
 * @param {Array} sessions - All sessions
 * @param {string} exerciseType - 'all', 'scale', or 'arpeggio'
 * @returns {Array} Filtered sessions
 */
export const filterSessionsByType = (sessions, exerciseType) => {
  if (!sessions || sessions.length === 0) return [];
  if (exerciseType === 'all') return sessions;
  
  return sessions.filter(s => s.exercise_type === exerciseType);
};

/**
 * Filter sessions by articulation type
 * @param {Array} sessions - All sessions
 * @param {string} articulationType - 'all', 'staccato', 'legato', or 'portato'
 * @returns {Array} Filtered sessions
 */
export const filterSessionsByArticulationType = (sessions, articulationType) => {
  if (!sessions || sessions.length === 0) return [];
  if (articulationType === 'all') return sessions;
  
  return sessions.filter(s => s.articulation_type === articulationType);
};

/**
 * Get the last N sessions
 * @param {Array} sessions - All sessions (sorted by date desc)
 * @param {number} n - Number of sessions to get
 * @returns {Array} Last N sessions
 */
export const getLastNSessions = (sessions, n) => {
  if (!sessions || sessions.length === 0) return [];
  return sessions.slice(0, n);
};

/**
 * Calculate average of a field across sessions
 * @param {Array} sessions - Sessions to average
 * @param {string} field - Field name to average
 * @returns {number} Average value
 */
export const calculateAverage = (sessions, field) => {
  if (!sessions || sessions.length === 0) return null;
  
  const validSessions = sessions.filter(s => s[field] != null);
  if (validSessions.length === 0) return null;
  
  const sum = validSessions.reduce((acc, s) => acc + s[field], 0);
  return sum / validSessions.length;
};

/**
 * Calculate median of a field across sessions
 * @param {Array} sessions - Sessions
 * @param {string} field - Field name
 * @returns {number} Median value
 */
export const calculateMedian = (sessions, field) => {
  if (!sessions || sessions.length === 0) return null;
  
  const values = sessions
    .filter(s => s[field] != null)
    .map(s => s[field])
    .sort((a, b) => a - b);
  
  if (values.length === 0) return null;
  
  const mid = Math.floor(values.length / 2);
  return values.length % 2 === 0
    ? (values[mid - 1] + values[mid]) / 2
    : values[mid];
};

/**
 * Calculate trend (last 5 avg vs last 30 avg)
 * @param {Array} sessions - All sessions
 * @param {string} field - Field name to calculate trend for
 * @returns {Object} {last5, last30, difference}
 */
export const calculateTrend = (sessions, field) => {
  if (!sessions || sessions.length === 0) {
    return { last5: null, last30: null, difference: null };
  }
  
  const last5 = calculateAverage(getLastNSessions(sessions, 5), field);
  const last30 = calculateAverage(getLastNSessions(sessions, 30), field);
  
  return {
    last5,
    last30,
    difference: last5 != null && last30 != null ? last5 - last30 : null
  };
};

/**
 * Calculate standard deviation of duration delta for a finger
 * @param {Array} sessions - Sessions to analyze
 * @param {string} hand - 'left' or 'right'
 * @param {number} finger - Finger number (1-5)
 * @returns {number} Standard deviation of duration delta
 */
export const calculateFingerDurationStdDev = (sessions, hand, finger) => {
  if (!sessions || sessions.length === 0) return null;
  
  const field = `${hand}_finger_${finger}_relative_duration_std_dev`;
  const validSessions = sessions.filter(s => s[field] != null);
  
  if (validSessions.length === 0) return null;
  
  // Average the std devs across sessions
  const sum = validSessions.reduce((acc, s) => acc + s[field], 0);
  return sum / validSessions.length;
};

/**
 * Calculate average duration delta for a finger
 * @param {Array} sessions - Sessions to analyze
 * @param {string} hand - 'left' or 'right'
 * @param {number} finger - Finger number (1-5)
 * @returns {number} Average duration delta (percentage)
 */
export const calculateFingerDurationDelta = (sessions, hand, finger) => {
  if (!sessions || sessions.length === 0) return null;
  
  const field = `${hand}_finger_${finger}_relative_duration_avg`;
  return calculateAverage(sessions, field);
};

/**
 * Calculate average duration delta for hand overall
 * @param {Array} sessions - Sessions to analyze
 * @param {string} hand - 'left' or 'right'
 * @returns {number} Average duration delta (percentage)
 */
export const calculateHandDurationDelta = (sessions, hand) => {
  if (!sessions || sessions.length === 0) return null;
  
  const field = `${hand}_relative_duration_avg`;
  return calculateAverage(sessions, field);
};

/**
 * Get fingers with worst duration consistency (highest std dev)
 * @param {Array} sessions - Sessions to analyze
 * @param {string} hand - 'left' or 'right'
 * @returns {Object} {mostInconsistent: {finger, stdDev}, mostConsistent: {finger, stdDev}}
 */
export const getMostInconsistentFingers = (sessions, hand) => {
  if (!sessions || sessions.length === 0) {
    return { mostInconsistent: null, mostConsistent: null };
  }
  
  const fingerStdDevs = [];
  
  for (let i = 1; i <= 5; i++) {
    const stdDev = calculateFingerDurationStdDev(sessions, hand, i);
    if (stdDev !== null) {
      fingerStdDevs.push({ finger: i, stdDev });
    }
  }
  
  if (fingerStdDevs.length === 0) {
    return { mostInconsistent: null, mostConsistent: null };
  }
  
  fingerStdDevs.sort((a, b) => a.stdDev - b.stdDev);
  
  const mostConsistent = fingerStdDevs[0];
  const mostInconsistent = fingerStdDevs[fingerStdDevs.length - 1];
  
  return { mostInconsistent, mostConsistent };
};

/**
 * Get fingers that miss articulation most often (longest and shortest duration deltas)
 * @param {Array} sessions - Sessions to analyze
 * @param {string} hand - 'left' or 'right'
 * @returns {Object} {longest: {finger, avg}, shortest: {finger, avg}}
 */
export const getBestWorstFingers = (sessions, hand) => {
  if (!sessions || sessions.length === 0) {
    return { longest: null, shortest: null };
  }
  
  const fingerDeltas = [];
  
  for (let i = 1; i <= 5; i++) {
    const avg = calculateFingerDurationDelta(sessions, hand, i);
    if (avg !== null) {
      fingerDeltas.push({ finger: i, avg });
    }
  }
  
  if (fingerDeltas.length === 0) {
    return { longest: null, shortest: null };
  }
  
  // Sort by actual value (not absolute value)
  fingerDeltas.sort((a, b) => a.avg - b.avg);
  
  const shortest = fingerDeltas[0]; // Most negative
  const longest = fingerDeltas[fingerDeltas.length - 1]; // Most positive
  
  // Return the extremes, even if they're small
  return {
    longest: longest,
    shortest: shortest
  };
};

/**
 * Get all unique exercises of a specific type from sessions
 * @param {Array} sessions - All sessions
 * @param {string} exerciseType - Exercise type
 * @returns {Array} Unique exercise names
 */
export const getExercisesByType = (sessions, exerciseType) => {
  if (!sessions || sessions.length === 0) return [];
  
  const exercisesOfType = sessions.filter(s => s.exercise_type === exerciseType);
  const uniqueExercises = [...new Set(exercisesOfType.map(s => s.exercise_name))];
  return uniqueExercises.sort();
};

/**
 * Calculate heatmap color for duration delta (bidirectional)
 * Orange for too long (positive), purple for too short (negative), white for perfect (0)
 * @param {number} value - Duration delta value (percentage)
 * @returns {string} RGB color string
 */
export const calculateHeatmapColor = (value) => {
  if (value == null) return 'rgb(255, 255, 255)'; // white for no data
  
  const absValue = Math.abs(value);
  const intensity = Math.min(absValue / 20, 1); // normalize to 0-1 (20% = full intensity)
  
  if (value > 0) {
    // Too long - orange (more intense orange as value increases)
    // Orange color: rgb(234, 88, 12) or #ea580c
    const r = 255;
    const g = Math.round(255 - (255 - 88) * intensity);
    const b = Math.round(255 - (255 - 12) * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (value < 0) {
    // Too short - purple (more intense purple as value decreases)
    // Purple color: rgb(147, 51, 234) or #9333ea
    const r = Math.round(255 - (255 - 147) * intensity);
    const g = Math.round(255 - (255 - 51) * intensity);
    const b = 255;
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Perfect - white
    return 'rgb(255, 255, 255)';
  }
};

/**
 * Linear regression calculation
 * @param {Array} points - Array of {x, y} points
 * @returns {Object} {slope, intercept}
 */
export const calculateLinearRegression = (points) => {
  if (!points || points.length < 2) return null;
  
  const n = points.length;
  const sumX = points.reduce((acc, p) => acc + p.x, 0);
  const sumY = points.reduce((acc, p) => acc + p.y, 0);
  const sumXY = points.reduce((acc, p) => acc + p.x * p.y, 0);
  const sumX2 = points.reduce((acc, p) => acc + p.x * p.x, 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  return { slope, intercept };
};

/**
 * Prepare data for trend over time chart
 * @param {Array} sessions - All sessions (30 days)
 * @param {string} hand - 'left' or 'right'
 * @param {Array} fingers - Array of finger numbers to plot
 * @returns {Object} Data organized by finger
 */
export const prepareTrendOverTimeData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_relative_duration_avg`;
    const points = sessions
      .filter(s => s[field] != null)
      .map(s => ({
        x: new Date(s.created_at).getTime(),
        y: s[field],
        date: s.created_at
      }));
    
    const regression = calculateLinearRegression(points);
    
    data[finger] = { points, regression };
  });
  
  return data;
};

/**
 * Prepare data for trend over tempo chart
 * @param {Array} sessions - All sessions (30 days)
 * @param {string} hand - 'left' or 'right'
 * @param {Array} fingers - Array of finger numbers to plot
 * @returns {Object} Data organized by finger
 */
export const prepareTrendOverTempoData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_relative_duration_avg`;
    const points = sessions
      .filter(s => s[field] != null && s.calculated_tempo != null)
      .map(s => ({
        x: s.calculated_tempo,
        y: s[field]
      }));
    
    data[finger] = { points };
  });
  
  return data;
};

/**
 * Prepare data for trend over repetition chart
 * @param {Array} sessions - All sessions (30 days)
 * @param {string} hand - 'left' or 'right'
 * @param {Array} fingers - Array of finger numbers to plot
 * @returns {Object} Data organized by finger
 */
export const prepareTrendOverRepetitionData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_relative_duration_avg`;
    const points = sessions
      .filter(s => s[field] != null)
      .map((s, idx) => ({
        x: sessions.length - idx, // Reverse so 1 is most recent
        y: s[field],
        date: s.created_at
      }))
      .reverse(); // Reverse so repetition 1 is oldest
    
    const regression = calculateLinearRegression(points);
    
    data[finger] = { points, regression };
  });
  
  return data;
};

/**
 * Calculate duration delta by direction
 * @param {Array} sessions - Sessions to analyze
 * @param {string} hand - 'left' or 'right'
 * @param {number} finger - Finger number (1-5), or null for hand overall
 * @param {string} direction - 'up' or 'down'
 * @returns {number} Average duration delta (percentage)
 */
export const calculateDirectionDurationDelta = (sessions, hand, finger, direction) => {
  if (!sessions || sessions.length === 0) return null;
  
  const prefix = finger ? `${hand}_finger_${finger}_${direction}` : `${hand}_${direction}`;
  const field = `${prefix}_relative_duration_avg`;
  
  return calculateAverage(sessions, field);
};

/**
 * Calculate percentage of sessions in duration delta range
 * @param {Array} sessions - Sessions to analyze
 * @param {string} field - Field to check
 * @param {number} threshold - Maximum absolute delta to be "in range" (default 5%)
 * @returns {number} Percentage (0-100)
 */
export const calculateInRangePercentage = (sessions, field, threshold = 5) => {
  if (!sessions || sessions.length === 0) return null;
  
  const validSessions = sessions.filter(s => s[field] != null);
  if (validSessions.length === 0) return null;
  
  const inRange = validSessions.filter(s => Math.abs(s[field]) <= threshold);
  return (inRange.length / validSessions.length) * 100;
};

/**
 * Calculate standard deviation for a field
 * @param {Array} sessions - Sessions to analyze
 * @param {string} field - Field name
 * @returns {number} Standard deviation
 */
export const calculateStdDev = (sessions, field) => {
  if (!sessions || sessions.length === 0) return null;
  
  const values = sessions.filter(s => s[field] != null).map(s => s[field]);
  if (values.length === 0) return null;
  
  const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
  return Math.sqrt(variance);
};

